import { useEffect, useRef, useState } from "react";
import {
	encodeShare,
	extractShare,
	selectShare,
	tripShareText,
	type TripWorkspace,
	type SharePayload,
} from "@japangolmok/core";
import { isAppsInTossChannel } from "../api-url";
import { track } from "../analytics/events";
import { BottomSheet } from "../ui/BottomSheet";
import { downloadTripFile } from "./TripTransfer";
import {
	serializeTripSelection,
	privateExportDefaults,
} from "../storage/transfer";
import type { PersistTrip } from "./TripExperience";
declare global {
	interface Window {
		__japangolmokShare?: (message: string) => Promise<unknown>;
		__japangolmokCreateShareLink?: (path: string) => Promise<string>;
	}
}
export async function channelShareLink(code: string) {
	if (isAppsInTossChannel()) {
		if (!window.__japangolmokCreateShareLink)
			throw new Error("TOSS_SHARE_UNAVAILABLE");
		return window.__japangolmokCreateShareLink(
			`intoss://japangolmok/s?d=${encodeURIComponent(code)}`,
		);
	}
	return `https://japangolmok.com/s#${code}`;
}
export async function shareMessage(text: string) {
	if (isAppsInTossChannel()) {
		if (!window.__japangolmokShare) throw new Error("TOSS_SHARE_UNAVAILABLE");
		await window.__japangolmokShare(text);
	} else if (navigator.share) await navigator.share({ text });
	else await navigator.clipboard.writeText(text);
}
export function CopyText({
	text,
	label = "글로 복사",
}: {
	text: string;
	label?: string;
}) {
	const [manual, setManual] = useState(false),
		[notice, setNotice] = useState("");
	return (
		<>
			<button
				className="button secondary"
				type="button"
				onClick={async () => {
					try {
						await navigator.clipboard.writeText(text);
						setNotice("복사했어요. 보낼 곳에 붙여 넣어 주세요.");
					} catch {
						setManual(true);
						setNotice("아래 글을 길게 눌러 복사해 주세요.");
					}
				}}
			>
				{label}
			</button>
			{manual && (
				<textarea
					readOnly
					aria-label="직접 복사할 글"
					value={text}
					rows={6}
					onFocus={(e) => e.currentTarget.select()}
				/>
			)}
			<p role="status">{notice}</p>
		</>
	);
}
export function SendPayload({
	payload,
	text,
}: {
	payload: SharePayload;
	text: string;
}) {
	const [result, setResult] = useState(""),
		[fallback, setFallback] = useState("");
	return (
		<>
			<button
				type="button"
				className="button primary"
				onClick={async () => {
					try {
						const code = await encodeShare(payload);
						if (code.length > 8000) throw new Error("SHARE_TOO_LONG");
						const link = await channelShareLink(code);
						setFallback(`${text}\n${link}`);
						await shareMessage(`${text}\n${link}`);
						setResult(
							"공유창으로 보냈어요. 상대에게 전달됐는지 확인해 주세요.",
						);
					} catch {
						setFallback(text);
						setResult("링크를 보내지 못했어요. 아래 글을 복사해 주세요.");
					}
				}}
			>
				공유하기
			</button>
			{fallback && <CopyText text={fallback} />}
			<p role="status">{result}</p>
		</>
	);
}
export function PasteShare() {
	const [error, setError] = useState("");
	return (
		<details className="workspace-disclosure">
			<summary>받은 링크 붙여 넣기</summary>
			<form
				className="compact-form"
				onSubmit={(e) => {
					e.preventDefault();
					try {
						const code = extractShare(
							String(new FormData(e.currentTarget).get("link")),
						);
						window.location.assign(`/s#${code}`);
					} catch {
						setError(
							"일본골목 링크가 없어요. 카톡에서 링크를 길게 눌러 복사한 뒤 붙여 넣어 주세요.",
						);
					}
				}}
			>
				<label>
					받은 일정·답장·정산 링크
					<textarea name="link" required maxLength={100000} />
				</label>
				<button type="submit" className="button secondary">
					열기
				</button>
			</form>
			{error && <p role="alert">{error}</p>}
		</details>
	);
}
export function ShareSheet({
	trip,
	persist,
}: {
	trip: TripWorkspace;
	persist: PersistTrip;
}) {
	const [open, setOpen] = useState(false),
		[stay, setStay] = useState(false),
		[code, setCode] = useState(""),
		[short, setShort] = useState(false),
		[error, setError] = useState(""),
		[message, setMessage] = useState(""),
		[prepared, setPrepared] = useState<{ payload: ReturnType<typeof selectShare>; encoded: string; message: string } | null>(null),
		[notice, setNotice] = useState(""),
		[busy, setBusy] = useState(false);
	const sharing = useRef(false);
	useEffect(() => {
		let cancelled = false;
		setPrepared(null);
		if (open && !isAppsInTossChannel()) {
			const payload = selectShare(trip, stay, short);
			void encodeShare(payload).then((encoded) => {
				if (!cancelled) setPrepared({ payload, encoded, message: `${tripShareText(payload.trip)}\nhttps://japangolmok.com/s#${encoded}` });
			}).catch(() => { if (!cancelled) setError("공유 내용을 만들지 못했어요. 파일로 보내 주세요."); });
		}
		return () => { cancelled = true; };
	}, [open, trip, stay, short]);
	async function prepare() {
		if (sharing.current) return;
		sharing.current = true; setBusy(true);
		try { await performShare(); } finally { sharing.current = false; setBusy(false); }
	}
	async function performShare() {
		if (!isAppsInTossChannel()) {
			if (!prepared) return;
			if (prepared.encoded.length > 8000) { setError("링크가 길어요. 장소 이름만 담거나 파일로 보내 주세요."); setShort(true); return; }
			setMessage(prepared.message); setCode(prepared.encoded); setError(""); setNotice("");
			try {
				await shareMessage(prepared.message);
				setNotice(typeof navigator.share === "function" ? "공유창을 열었어요." : "복사했어요. 보낼 곳에 붙여 넣어 주세요.");
				if (await persist((d) => { d.shareId = prepared.payload.shareId; d.shareRevision = prepared.payload.revision; }) === false) setError("공유 내용은 준비됐지만 이 기기에 공유 기록을 저장하지 못했어요.");
				track("trip_share_link_created");
			} catch (cause) {
				if (typeof cause === "object" && cause !== null && "name" in cause && cause.name === "AbortError") setNotice("공유를 취소했어요. 내용은 그대로예요.");
				else setError("공유창을 열지 못했어요. 아래 글로 복사해 주세요.");
			}
			return;
		}
		try {
			const payload = selectShare(trip, stay, short),
				encoded = await encodeShare(payload);
			if (encoded.length > 8000) {
				setError("링크가 길어요. 장소 이름만 담거나 파일로 보내 주세요.");
				setShort(true);
				return;
			}
			if (
				(await persist((d) => {
					d.shareId = payload.shareId;
					d.shareRevision = payload.revision;
				})) === false
			)
				return;
			setCode(encoded);
			try {
				const link = await channelShareLink(encoded);
				const message = `${tripShareText(payload.trip)}\n${link}`;
				setMessage(message);
				try {
					await shareMessage(message);
				} catch (cause) {
					if (typeof cause === "object" && cause !== null && "name" in cause && cause.name === "AbortError") setNotice("공유를 취소했어요. 내용은 그대로예요.");
					else setError("공유창을 열지 못했어요. 아래 글로 복사하거나 다시 공유해 주세요.");
				}
				track("trip_share_link_created");
			} catch {
				setMessage(tripShareText(payload.trip));
				setError(
					"토스 링크를 만들지 못했어요. 웹 주소 없이 글이나 파일로 보내 주세요.",
				);
			}
		} catch {
			setError(
				"공유 내용을 만들지 못했어요. 장소를 줄이거나 파일로 보내 주세요.",
			);
		}
	}
	return (
		<>
			<button
				className="button primary"
				type="button"
				disabled={!trip.days.some((d) => d.items.length)}
				onClick={() => {
					setOpen(true);
					setNotice("");
					setCode("");
					setMessage("");
				}}
			>
				일행에게 보내기
			</button>
			{!trip.days.some((d) => d.items.length) && (
				<p>장소를 하나 이상 담으면 보낼 수 있어요.</p>
			)}
			{open && (
				<BottomSheet
					title="보낼 내용을 확인해요"
					onClose={() => setOpen(false)}
				>
					<p>
						보내기를 누른 내용만 링크 안에 담겨요. 일본골목 서버에는 저장되지
						않지만 링크를 가진 사람과 메신저는 볼 수 있어요.
					</p>
					<p>
						날짜·장소·시간·담당·별명을 담아요. 금액·자유메모·사진·숙소 주소는
						기본으로 빼요. 식사 카드는 항상 제외해요.
					</p>
					<label className="share-check">
						<input
							type="checkbox"
							checked={stay}
							onChange={(e) => {
								setStay(e.target.checked);
								setCode("");
								setMessage("");
							}}
						/>{" "}
						<span>숙소 이름·일본어 주소도 담기</span>
					</label>
					<label className="share-check">
						<input
							type="checkbox"
							checked={short}
							onChange={(e) => {
								setShort(e.target.checked);
								setCode("");
								setMessage("");
							}}
						/>{" "}
						<span>장소 이름만 담은 짧은 링크</span>
					</label>
					<ol>
						{trip.days.map((d) => (
							<li key={d.date}>
								{new Intl.DateTimeFormat("ko-KR", {month:"long", day:"numeric", weekday:"short", timeZone:"UTC"}).format(new Date(`${d.date}T00:00:00Z`))} ·{" "}
								{d.items
									.filter((i) => !i.deferred)
									.map((i) => i.title)
									.join(" → ")}
							</li>
						))}
					</ol>
					<button
						type="button"
						className="button secondary"
						disabled={busy || (!isAppsInTossChannel() && !prepared)}
						onClick={() => void prepare()}
					>
						확인하고 공유하기
					</button>
					{notice && <p role="status">{notice}</p>}
					{code && message && error && <label>직접 복사할 공유 글<textarea readOnly value={message} rows={4} onFocus={(event) => event.currentTarget.select()} /></label>}
					{code && message && (
						<>
							<button
								type="button"
								className="button primary"
								disabled={busy}
								onClick={async () => {
									if (sharing.current) return;
									sharing.current = true; setBusy(true);
									try { await shareMessage(message); }
									catch (cause) { if (typeof cause === "object" && cause !== null && "name" in cause && cause.name === "AbortError") setNotice("공유를 취소했어요. 내용은 그대로예요."); else setError("공유창을 열지 못했어요. 글로 복사해 주세요."); }
									finally { sharing.current = false; setBusy(false); }
								}}
							>
								일행에게 공유하기
							</button>
							<CopyText text={message} />
						</>
					)}
					<button
						type="button"
						className="button text"
						onClick={() =>
							downloadTripFile(
								"japangolmok-trip-copy.json",
								serializeTripSelection(trip, privateExportDefaults),
							)
						}
					>
						파일로 보내기
					</button>
					<p role="alert">{error}</p>
				</BottomSheet>
			)}
			<PasteShare />
		</>
	);
}
export function PartyEditor({
	trip,
	persist,
}: {
	trip: TripWorkspace;
	persist: PersistTrip;
}) {
	const [error, setError] = useState("");
	const party =
		trip.party ??
		Array.from({ length: trip.partySize }, (_, i) => ({
			id: i === 0 ? "me" : `party-${i}`,
			nickname: i === 0 ? "나" : `일행${i}`,
		}));
	return (
		<details className="workspace-disclosure">
			<summary>일행 별명 고치기</summary>
			<form
				onSubmit={async (e) => {
					e.preventDefault();
					const d = new FormData(e.currentTarget);
					const next = party.map((p) => ({
						...p,
						nickname: String(d.get(p.id)).trim().slice(0, 10) || p.nickname,
					}));
					const ok = await persist((t) => {
						t.party = next;
					});
					if (ok === false)
						setError("별명을 저장하지 못했어요. 다시 눌러 주세요.");
				}}
			>
				{party.map((p) => (
					<label key={p.id}>
						별명 · 실명 대신 부르는 이름
						<input name={p.id} maxLength={10} defaultValue={p.nickname} />
					</label>
				))}
				<button type="submit" className="button secondary">
					일행 별명 저장
				</button>
			</form>
			{party.length < trip.partySize && (
				<button
					type="button"
					className="button secondary"
					onClick={() =>
						void persist((t) => {
							t.party = [
								...party,
								{ id: crypto.randomUUID(), nickname: `일행${party.length}` },
							];
						})
					}
				>
					일행 별명 더하기
				</button>
			)}
			{party.slice(1).map((p) => (
				<button
					type="button"
					key={p.id}
					className="button text"
					onClick={async () => {
						if (
							!window.confirm(
								"이 별명을 지우면 지출과 담당을 나에게 옮겨요. 계속할까요?",
							)
						)
							return;
						await persist((t) => {
							const me = party[0]!.id;
							t.party = party.filter((x) => x.id !== p.id);
							t.expenses.forEach((x) => {
								if (x.paidBy === p.id) x.paidBy = me;
								x.splitWith = x.splitWith
									?.map((id) => (id === p.id ? me : id))
									.filter((id, i, a) => a.indexOf(id) === i);
							});
							t.checklist.forEach((x) => {
								if (x.assignee === p.id) x.assignee = me;
							});
						});
					}}
				>
					별명 {p.nickname} 지우기
				</button>
			))}
			{error && <p role="alert">{error}</p>}
		</details>
	);
}
